// =============================================================================
// SIREPPAR - Consultas de partidos: la SEP inicia una consulta
// =============================================================================
// La consulta queda esperando la respuesta del partido, que recibe un aviso en
// el panel y un correo. El expediente es opcional y tiene que ser de ese partido.
// =============================================================================

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import RichTextEditor from '../../components/common/RichTextEditor';
import AdjuntosInforme, { MAX_ADJUNTOS, MAX_MB_ADJUNTO } from '../../components/common/AdjuntosInforme';
import { consultaSepService, expedienteService, partidoService } from '../../services/api';
import { CLAVE_CONSULTAS_PENDIENTES } from '../../hooks/useConsultasPendientes';
import { tipoTramiteLabel } from '../../utils/tiposExpediente';
import { ASUNTO_MAX, ASUNTO_MIN, TEXTO_MAX, mensajeDeError } from '../../utils/consultas';

interface PartidoOpcion {
  id: string;
  denominacion: string;
  numero: number | null;
}

interface ExpedienteOpcion {
  id: string;
  numero: string;
  tipo: string;
}

export default function GestionConsultaNuevaPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [partidoId, setPartidoId] = useState('');
  const [expedienteId, setExpedienteId] = useState('');
  const [asunto, setAsunto] = useState('');
  const [texto, setTexto] = useState('');
  const [archivos, setArchivos] = useState<File[]>([]);

  const { data: partidos = [] } = useQuery<PartidoOpcion[]>({
    queryKey: ['sep-consultas-partidos'],
    queryFn: async () => {
      const res = await partidoService.list({ pageSize: 500 });
      return res.data?.data?.items ?? res.data?.data ?? [];
    },
  });

  const { data: expedientes = [], isFetching: cargandoExpedientes } = useQuery<ExpedienteOpcion[]>({
    queryKey: ['sep-consultas-expedientes', partidoId],
    queryFn: async () => {
      const res = await expedienteService.list({ partidoId, pageSize: 100 });
      return res.data?.data?.items ?? res.data?.data ?? [];
    },
    enabled: !!partidoId,
  });

  const enviar = useMutation({
    mutationFn: () => {
      const datos = new FormData();
      datos.append('partidoId', partidoId);
      datos.append('asunto', asunto.trim());
      datos.append('texto', texto.trim());
      if (expedienteId) datos.append('expedienteId', expedienteId);
      archivos.forEach((archivo) => datos.append('archivos', archivo));
      return consultaSepService.crear(datos);
    },
    onSuccess: (res) => {
      toast.success('Consulta enviada al partido');
      queryClient.invalidateQueries({ queryKey: ['sep-consultas'] });
      queryClient.invalidateQueries({ queryKey: CLAVE_CONSULTAS_PENDIENTES });
      navigate(res?.data?.id ? `/gestion-consultas/${res.data.id}` : '/gestion-consultas');
    },
    onError: (e) => toast.error(mensajeDeError(e, 'No se pudo enviar la consulta')),
  });

  const asuntoLimpio = asunto.trim();
  const textoLimpio = texto.trim();
  const errorAsunto = asuntoLimpio.length > 0 && asuntoLimpio.length < ASUNTO_MIN
    ? `El asunto tiene que tener al menos ${ASUNTO_MIN} caracteres.`
    : asuntoLimpio.length > ASUNTO_MAX ? `El asunto puede tener hasta ${ASUNTO_MAX} caracteres.` : '';
  const errorTexto = textoLimpio.length > TEXTO_MAX ? `La consulta puede tener hasta ${TEXTO_MAX} caracteres.` : '';
  const puedeEnviar = !!partidoId && asuntoLimpio.length >= ASUNTO_MIN && !errorAsunto
    && textoLimpio.length > 0 && !errorTexto && !enviar.isPending;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Nueva consulta a un partido</h1>
        <p className="text-sm text-gray-600 mt-2">
          La consulta le llega al partido con un aviso en su panel y un correo, y queda esperando su respuesta.
        </p>
      </div>

      <form
        className="bg-white rounded-lg border border-gray-200 p-6 space-y-5"
        onSubmit={(e) => { e.preventDefault(); if (puedeEnviar) enviar.mutate(); }}
      >
        <div>
          <label htmlFor="partido" className="block text-sm font-medium text-gray-700 mb-1">Partido o alianza</label>
          <select
            id="partido"
            value={partidoId}
            onChange={(e) => { setPartidoId(e.target.value); setExpedienteId(''); }}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">Elegí una agrupación</option>
            {partidos.map((p) => (
              <option key={p.id} value={p.id}>{p.numero != null ? `${p.numero} — ` : ''}{p.denominacion}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="expediente" className="block text-sm font-medium text-gray-700 mb-1">Expediente (opcional)</label>
          <select
            id="expediente"
            value={expedienteId}
            onChange={(e) => setExpedienteId(e.target.value)}
            disabled={!partidoId || cargandoExpedientes}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-50"
          >
            <option value="">{partidoId ? 'Sin expediente' : 'Primero elegí la agrupación'}</option>
            {expedientes.map((e) => (
              <option key={e.id} value={e.id}>{e.numero} — {tipoTramiteLabel(e.tipo)}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="asunto" className="block text-sm font-medium text-gray-700 mb-1">Asunto</label>
          <input
            id="asunto"
            value={asunto}
            onChange={(e) => setAsunto(e.target.value)}
            maxLength={ASUNTO_MAX}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Tema de la consulta"
          />
          {errorAsunto && <p className="text-xs text-red-600 mt-1">{errorAsunto}</p>}
        </div>

        <div>
          <span className="block text-sm font-medium text-gray-700 mb-1">Consulta</span>
          <RichTextEditor value={texto} onChange={setTexto} placeholder="Escribí la consulta para el partido" minHeight={180} />
          {errorTexto && <p className="text-xs text-red-600 mt-1">{errorTexto}</p>}
        </div>

        <AdjuntosInforme
          archivos={archivos}
          onChange={setArchivos}
          ayuda={`Hasta ${MAX_ADJUNTOS} archivos PDF o imagen, de ${MAX_MB_ADJUNTO} MB cada uno.`}
        />

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!puedeEnviar}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
          >
            {enviar.isPending ? 'Enviando...' : 'Enviar consulta'}
          </button>
        </div>
      </form>
    </div>
  );
}
